import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import './PinGolfRules.css';
// The injected markup carries PinGolfScore's own class names for the under/over/doubled score
// chips. Nothing imports the component here any more, so the stylesheet has to be pulled in
// directly or the chips render as plain unstyled text wherever this is used.
import './PinGolfScore.css';

// The complete player-facing league rules. The prose itself lives on the server, in
// server/src/config/pingolfRulesContent.js, and this component just renders what comes back.
//
// It used to be ~340 lines of JSX here, which meant the same sentences had to be kept in step
// by hand with docs/PENTHOUSE_LEAGUE_RULES.md and would have needed keeping in step a third
// time with the club's WordPress page. One renderer, many consumers: the app fetches it, and
// wordpressApiService pushes the standalone variant to WordPress.
//
// Moving it also let the scoring and payout tables be COMPUTED from the real engine rather
// than transcribed - the numbers a player reads here now come from the same functions that
// score a hole and pay a week out.
//
// The HTML is our own server's output, built from a fixed template with escaped season values,
// so injecting it is safe. It arrives using the design system's own accordion class names
// (DSAccordionItem is a plain <details>/<summary>), which is what makes the injected markup
// indistinguishable from the component this replaced.

function PinGolfRules({ season, className = '' }) {
  const seasonId = season?.id;
  const [html, setHtml] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!seasonId) return undefined;

    // A season page that switches seasons mid-flight must not paint the previous season's
    // rules, and an unmounted one must not set state at all.
    let active = true;
    setFailed(false);

    apiService.getPingolfSeasonRules(seasonId)
      .then(response => {
        if (active) setHtml(response.data.html);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => { active = false; };
  }, [seasonId]);

  if (!season) return null;

  // The rules are long, and the page they sit on is worth reading without them. A failed or
  // pending fetch collapses to a single line rather than an error card or a skeleton the
  // height of the whole document.
  if (failed) {
    return (
      <div className={['pg-rules', className].filter(Boolean).join(' ')}>
        <p className="pg-rules-note">The league rules could not be loaded. Refresh to try again.</p>
      </div>
    );
  }

  if (html === null) {
    return (
      <div className={['pg-rules', className].filter(Boolean).join(' ')}>
        <p className="pg-rules-note">Loading the league rules...</p>
      </div>
    );
  }

  return (
    <div
      className={['pg-rules', className].filter(Boolean).join(' ')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default PinGolfRules;
